/**
 * RoomsTab: the property's room matrix.
 *
 * Spreadsheet-style grid of rooms. One row per physical room. Each row
 * has a name + integer counts for each catalogue item that goes into the
 * room (Beds Large/Small, Pillows Large/Small in simple mode; Toppers,
 * Electric Blankets, Headboards, Ottomans, Sofas, Cots, Chairs under
 * Show extras). The grid is the source of truth: triggers on hosp_rooms
 * derive bed_count from the items jsonb and keep hosp_properties
 * total_rooms + total_beds in step automatically.
 *
 * Auto-suggest: if items is empty across all rows and a proposal exists
 * for this property, the grid pre-fills with the proposal-derived row
 * set on first open and surfaces a banner asking the operator to
 * review + Save. A Suggest from upstream button is always available
 * for manual rerun. V1 source is the proposal. Invoice source is V1.1.
 *
 * Canonical admin styles only: form-input, form-label, btn, btn-primary,
 * btn-pos-gold, action-icon, alert, card. The only inline style is the
 * horizontal scroll container for advanced mode (the grid is wider than
 * the modal in advanced mode).
 *
 * See memory/feedback_canonical_layout_no_reinvention.md.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useToast } from '../../../../../components/Toast';
import { POS_GOLD } from '../constants';
import { ITEM_COLUMNS } from './rooms-matrix/itemColumns';
import { suggestFromProposal, suggestFromServiceLog, type ServiceLogItem, type SuggestedRow } from './rooms-matrix/suggestionEngines';
import type { EnrichedProperty, PropertyMode } from './types';

interface RoomsTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
  mode: PropertyMode;
  onRefresh?: () => Promise<void> | void;
  // Kept for API compatibility with the prior thin-wrapper; the matrix
  // grid does not surface per-room certificate / booking / service
  // sub-modals. Those remain available on PropertyRoomsView (the full
  // page Rooms view at Level 2 of POSProperties).
  setCertificatesContext?: unknown;
  setBookingsContext?: unknown;
  setServicesContext?: unknown;
}

interface RoomRow {
  /** uuid for existing rows. null/undefined for unsaved new rows. */
  id?: string | null;
  room_number: string;
  room_name: string;
  items: Record<string, number>;
}

/** Loaded shape from hosp_rooms. */
interface RawRoom {
  id: string;
  property_id: string;
  room_number: string;
  room_name: string | null;
  items: Record<string, number> | null;
}

function toRow(r: RawRoom): RoomRow {
  return {
    id: r.id,
    room_number: r.room_number || '',
    room_name: r.room_name || r.room_number || '',
    items: r.items && typeof r.items === 'object' ? { ...r.items } : {},
  };
}

function suggestionToRow(s: SuggestedRow): RoomRow {
  return { id: null, room_number: s.room_number, room_name: s.room_name, items: { ...s.items } };
}


function rowsEqual(a: RoomRow, b: RoomRow): boolean {
  if ((a.room_number || '') !== (b.room_number || '')) return false;
  if ((a.room_name || '') !== (b.room_name || '')) return false;
  const ak = Object.keys(a.items);
  const bk = Object.keys(b.items);
  const merged = new Set([...ak, ...bk]);
  for (const k of merged) {
    if ((a.items[k] || 0) !== (b.items[k] || 0)) return false;
  }
  return true;
}

export default function RoomsTab({ supabase, property, mode, onRefresh }: RoomsTabProps) {
  const { showToast } = useToast();
  const readOnly = mode === 'view';

  const [originalRows, setOriginalRows] = useState<RoomRow[]>([]);
  const [rows, setRows] = useState<RoomRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showExtras, setShowExtras] = useState(false);
  const [autoSuggestApplied, setAutoSuggestApplied] = useState(false);
  const [suggestModalOpen, setSuggestModalOpen] = useState(false);
  const [suggestSource, setSuggestSource] = useState<SuggestedRow[] | null>(null);
  const [autoSuggestSource, setAutoSuggestSource] = useState<'proposal' | 'service-log' | null>(null);
  const [proposalAvailable, setProposalAvailable] = useState(false);
  const [serviceLogSuggestion, setServiceLogSuggestion] = useState<SuggestedRow[]>([]);

  const visibleColumns = useMemo(
    () => ITEM_COLUMNS.filter((c) => c.mode === 'simple' || showExtras),
    [showExtras],
  );

  /** Load existing rooms + check for a proposal in parallel. */
  const loadData = useCallback(async () => {
    if (!property?.id) return;
    setLoading(true);
    try {
      // Load rooms + proposal + latest non-draft service log in parallel.
      // The service log query pulls the most recent log for this property,
      // which the suggestion engine then turns into rows when no proposal
      // exists (the common case for direct signups).
      const [roomsRes, proposalRes, latestLogRes] = await Promise.all([
        supabase
          .from('hosp_rooms')
          .select('id, property_id, room_number, room_name, items')
          .eq('property_id', property.id)
          .order('room_number', { ascending: true }),
        property.proposal_id
          ? supabase
              .from('pos_proposals')
              .select('id, proposal_data')
              .eq('id', property.proposal_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        supabase
          .from('service_logs')
          .select('id, service_date')
          .eq('property_id', property.id)
          .neq('status', 'draft')
          .order('service_date', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const loaded = ((roomsRes.data ?? []) as RawRoom[]).map(toRow);
      setOriginalRows(loaded);

      // Build a service-log suggestion when one exists.
      let logSuggestion: SuggestedRow[] = [];
      const latestLogId = (latestLogRes.data as { id?: string } | null)?.id ?? null;
      if (latestLogId) {
        const { data: itemRows } = await supabase
          .from('service_log_items')
          .select('section_label, section_quantity, item_name, quantity')
          .eq('service_log_id', latestLogId);
        logSuggestion = suggestFromServiceLog((itemRows ?? []) as ServiceLogItem[]);
      }
      setServiceLogSuggestion(logSuggestion);

      // Pre-apply only when the property has no rooms at all, i.e. a
      // brand-new property that has never had its room list set up.
      // Priority: proposal then service log. The operator reviews +
      // edits + Saves; Cancel reverts to the loaded state.
      // Once any rooms exist we must NOT pre-fill: doing so would throw
      // away the saved rooms on every open and revert the operator's
      // edits back to the proposal. The "Suggest from proposal" and
      // "Suggest from service log" buttons remain for manual use.
      // Skipped entirely in view mode — read-only views must reflect
      // what is actually saved, not a suggestion the operator cannot
      // commit, otherwise the "hit Save" banner shows with no Save
      // button to honour it.
      const noRoomsYet = loaded.length === 0;
      const proposalData = (proposalRes.data as { proposal_data?: unknown } | null)?.proposal_data;
      const proposalSuggestion = proposalData ? suggestFromProposal(proposalData as never) : [];
      setProposalAvailable(proposalSuggestion.length > 0);

      if (!readOnly && noRoomsYet && proposalSuggestion.length > 0) {
        setRows(proposalSuggestion.map(suggestionToRow));
        setAutoSuggestApplied(true);
        setAutoSuggestSource('proposal');
      } else if (!readOnly && noRoomsYet && logSuggestion.length > 0) {
        setRows(logSuggestion.map(suggestionToRow));
        setAutoSuggestApplied(true);
        setAutoSuggestSource('service-log');
      } else {
        setRows(loaded.map((r) => ({ ...r, items: { ...r.items } })));
        setAutoSuggestApplied(false);
        setAutoSuggestSource(null);
      }
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to load rooms', 'error');
    } finally {
      setLoading(false);
    }
  // showToast is stable from context, omitted to avoid reload loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, property?.id, property?.proposal_id, readOnly]);

  // Load once per property. The effect must NOT re-run on loadData
  // identity churn: a background re-render of the POS Properties page
  // (e.g. a session token refresh re-fetching the list) would otherwise
  // re-run loadData and overwrite the operator's unsaved row edits with
  // the saved values, leaving the Save button inactive. Explicit reloads
  // after a save call loadData() directly and are unaffected.
  const loadedPropertyIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!property?.id) return;
    if (loadedPropertyIdRef.current === property.id) return;
    loadedPropertyIdRef.current = property.id;
    loadData();
  }, [property?.id, loadData]);

  /** Diff local rows against original loaded rows for the save. */
  const dirty = useMemo(() => {
    if (rows.length !== originalRows.length) return true;
    if (autoSuggestApplied) return true;
    const byId = new Map(originalRows.filter((r) => r.id).map((r) => [r.id as string, r] as const));
    for (const r of rows) {
      if (!r.id) return true;
      const orig = byId.get(r.id);
      if (!orig) return true;
      if (!rowsEqual(orig, r)) return true;
    }
    return false;
  }, [rows, originalRows, autoSuggestApplied]);

  function addRow() {
    setRows((curr) => [
      ...curr,
      { id: null, room_number: `Room ${curr.length + 1}`, room_name: `Room ${curr.length + 1}`, items: {} },
    ]);
  }

  function removeRow(idx: number) {
    setRows((curr) => curr.filter((_, i) => i !== idx));
  }

  function updateName(idx: number, value: string) {
    setRows((curr) => curr.map((r, i) => i === idx
      ? { ...r, room_number: value, room_name: value }
      : r));
  }

  function updateItem(idx: number, key: string, value: string) {
    const n = Math.max(0, Math.floor(Number(value) || 0));
    setRows((curr) => curr.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r.items };
      if (n > 0) next[key] = n;
      else delete next[key];
      return { ...r, items: next };
    }));
  }

  function openSuggestFromProposal() {
    if (!proposalAvailable || !property.proposal_id) {
      showToast('No proposal linked to this property yet.', 'info');
      return;
    }
    // Recompute fresh suggestion in case proposal data has changed.
    supabase
      .from('pos_proposals')
      .select('proposal_data')
      .eq('id', property.proposal_id)
      .maybeSingle()
      .then(({ data }) => {
        const suggestion = suggestFromProposal((data as { proposal_data?: unknown } | null)?.proposal_data as never);
        setSuggestSource(suggestion);
        setSuggestModalOpen(true);
      });
  }

  function openSuggestFromServiceLog() {
    if (serviceLogSuggestion.length === 0) {
      showToast('No service log captured for this property yet.', 'info');
      return;
    }
    setSuggestSource(serviceLogSuggestion);
    setSuggestModalOpen(true);
  }

  function applySuggestion() {
    if (!suggestSource) return;
    setRows(suggestSource.map(suggestionToRow));
    setAutoSuggestApplied(true);
    setSuggestModalOpen(false);
    setSuggestSource(null);
  }

  function cancelAutoSuggest() {
    setRows(originalRows.map((r) => ({ ...r, items: { ...r.items } })));
    setAutoSuggestApplied(false);
  }

  async function handleSave() {
    if (!dirty || readOnly) return;
    setSaving(true);
    try {
      // Diff: figure out what to insert / update / delete.
      const localById = new Map(rows.filter((r) => r.id).map((r) => [r.id as string, r] as const));
      const newRows = rows.filter((r) => !r.id);
      const toDelete = originalRows
        .filter((r) => r.id && !localById.has(r.id))
        .map((r) => r.id as string);
      const toUpdate: RoomRow[] = [];
      for (const r of rows) {
        if (!r.id) continue;
        const orig = originalRows.find((o) => o.id === r.id);
        if (orig && !rowsEqual(orig, r)) toUpdate.push(r);
      }

      // Deletes first so we don't trip room_number uniqueness if a
      // suggestion reuses a name that an old row had.
      if (toDelete.length > 0) {
        const { error } = await supabase
          .from('hosp_rooms')
          .delete()
          .in('id', toDelete);
        if (error) throw error;
      }

      for (const r of toUpdate) {
        const { error } = await supabase
          .from('hosp_rooms')
          .update({
            room_number: r.room_number || 'Room',
            room_name: r.room_name || r.room_number || 'Room',
            items: r.items,
          })
          .eq('id', r.id as string);
        if (error) throw error;
      }

      if (newRows.length > 0) {
        const payload = newRows.map((r) => ({
          property_id: property.id,
          room_number: r.room_number || 'Room',
          room_name: r.room_name || r.room_number || 'Room',
          room_type: 'standard',
          status: 'active',
          items: r.items,
        }));
        const { error } = await supabase.from('hosp_rooms').insert(payload);
        if (error) throw error;
      }

      // Sync totals through the SECURITY DEFINER RPC. Direct REST
      // updates on hosp_properties were being silently swallowed for
      // some saves, and the AFTER trigger on hosp_rooms also did not
      // fire reliably from PostgREST. The RPC derives + writes from
      // inside the database in one call that cannot be denied.
      await supabase.rpc('pos_sync_property_totals', { p_property_id: property.id });

      showToast('Rooms saved.', 'success');
      setAutoSuggestApplied(false);
      await loadData();
      await onRefresh?.();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save rooms', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="text-muted fs-sm">Loading rooms…</div>;
  }

  return (
    <div>
      {autoSuggestApplied && (
        <div className="alert alert-info mb-md">
          <strong>
            Pre-filled from {autoSuggestSource === 'service-log' ? 'the latest service log' : 'proposal'}.
          </strong>{' '}
          Review the rows below and hit <strong>Save</strong> to commit.{' '}
          {!readOnly && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={cancelAutoSuggest}
              style={{ marginLeft: '0.5rem' }}
            >
              Discard suggestion
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-md flex-wrap mb-md">
        <div className="flex items-center gap-sm flex-wrap">
          {!readOnly && (
            <button type="button" className="btn btn-secondary" onClick={addRow}>
              + Add row
            </button>
          )}
          <label className="flex items-center gap-xs fs-sm" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showExtras}
              onChange={(e) => setShowExtras(e.target.checked)}
              style={{ accentColor: POS_GOLD }}
            />
            Show extras
          </label>
          {!readOnly && proposalAvailable && (
            <button type="button" className="btn" onClick={openSuggestFromProposal}>
              ✨ Suggest from proposal
            </button>
          )}
          {!readOnly && serviceLogSuggestion.length > 0 && (
            <button type="button" className="btn" onClick={openSuggestFromServiceLog}>
              ✨ Suggest from service log
            </button>
          )}
        </div>
        {!readOnly && (
          <button
            type="button"
            className="btn btn-primary btn-pos-gold"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        )}
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ minWidth: 180 }}>Room</th>
              {visibleColumns.map((c) => (
                <th key={c.key} className="text-center" style={{ minWidth: 70 }}>{c.label}</th>
              ))}
              {!readOnly && <th className="table-col-actions">{' '}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (readOnly ? 1 : 2)}
                  className="text-center text-muted fs-sm"
                  style={{ padding: '1.5rem' }}
                >
                  No rooms yet.{' '}
                  {!readOnly && (
                    <button type="button" className="btn btn-sm" onClick={addRow}>
                      Add the first row
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={r.id ?? `new-${idx}`}>
                  <td>
                    {readOnly ? (
                      <span>{r.room_name || r.room_number || '—'}</span>
                    ) : (
                      <input
                        type="text"
                        className="form-input"
                        value={r.room_name}
                        onChange={(e) => updateName(idx, e.target.value)}
                        placeholder="Room name"
                      />
                    )}
                  </td>
                  {visibleColumns.map((c) => (
                    <td key={c.key} className="text-center">
                      {readOnly ? (
                        <span>{r.items[c.key] || ''}</span>
                      ) : (
                        <input
                          type="number"
                          className="form-input text-center"
                          min={0}
                          value={r.items[c.key] || ''}
                          onChange={(e) => updateItem(idx, c.key, e.target.value)}
                        />
                      )}
                    </td>
                  ))}
                  {!readOnly && (
                    <td className="text-center">
                      <span
                        className="action-icon"
                        onClick={() => removeRow(idx)}
                        title="Delete row"
                        style={{ cursor: 'pointer' }}
                      >
                        🗑️
                      </span>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-muted fs-xs mt-sm">
        Bed counts and the property&apos;s room and bed totals derive from this matrix automatically when you save.
      </p>

      {suggestModalOpen && suggestSource && (
        <div className="modal-overlay" onClick={() => setSuggestModalOpen(false)}>
          <div className="modal modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Suggest rooms from proposal</h2>
              <button type="button" className="modal-close" onClick={() => setSuggestModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="text-muted fs-sm mb-md">
                Applying this will replace the current matrix with {suggestSource.length} row{suggestSource.length === 1 ? '' : 's'} from the proposal. You can edit before saving.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Room</th>
                      {ITEM_COLUMNS.filter((c) => c.mode === 'simple').map((c) => (
                        <th key={c.key} className="text-center">{c.label}</th>
                      ))}
                      <th className="text-center">Other</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestSource.map((s, i) => {
                      const otherCount = Object.entries(s.items)
                        .filter(([k]) => !['Beds: Large', 'Beds: Small', 'Pillows : Large', 'Pillows : Small'].includes(k))
                        .reduce((sum, [, v]) => sum + (v || 0), 0);
                      return (
                        <tr key={i}>
                          <td>{s.room_name}</td>
                          {ITEM_COLUMNS.filter((c) => c.mode === 'simple').map((c) => (
                            <td key={c.key} className="text-center">{s.items[c.key] || ''}</td>
                          ))}
                          <td className="text-center text-muted">{otherCount || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn" onClick={() => setSuggestModalOpen(false)}>Cancel</button>
              <button type="button" className="btn btn-primary btn-pos-gold" onClick={applySuggestion}>
                Apply, then review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
